import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const AddOrEditBanner = () => {
  const { id } = useParams(); // If present, we're editing
  const navigate = useNavigate();

  const [bannerFile, setBannerFile] = useState(null);
  const [existingImage, setExistingImage] = useState(""); // for preview
  const [orderNo, setOrderNo] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Fetch existing banner data for edit
  useEffect(() => {
    const fetchBanner = async () => {
      if (!id) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_URL_API}/banner/${id}`);
        const banner = res.data;
        console.log("Fetched banner data:", banner);
        setOrderNo(banner?.data?.order_no);
        setIsEnabled(banner?.data?.is_enabled);
        setExistingImage(banner?.data?.url);
      } catch (err) {
        console.error("Failed to load banner for editing", err);
        setMessage("Failed to load banner.");
      }
    };
    fetchBanner();
  }, [id]);

  const handleFileChange = (e) => {
    setBannerFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const formData = new FormData();
      if (bannerFile) formData.append("banner", bannerFile);
      formData.append("order_no", orderNo);
      formData.append("is_enabled", isEnabled);

      if (id) {
        // Update
        await axios.patch(`${process.env.REACT_APP_BASE_URL_API}/banner/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("Banner updated successfully!");
      } else {
        // Create
        await axios.post(`${process.env.REACT_APP_BASE_URL_API}/banner`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("Banner uploaded successfully!");
      }

      setTimeout(() => navigate("/banners"), 1500); // Redirect after success
    } catch (error) {
      console.error("Error submitting banner:", error);
      setMessage("Failed to submit banner.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this banner?");
    if (!confirmDelete) return;

    try {
      setLoading(true);
      await axios.delete(`${process.env.REACT_APP_BASE_URL_API}/banner/${id}`);
      setMessage("Banner deleted successfully!");
      setTimeout(() => navigate("/banners"), 1500);
    } catch (error) {
      console.error("Failed to delete banner:", error);
      setMessage("Failed to delete banner.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="ml-0 lg:ml-[230px] mt-4 bg-[#F5F5F4] pl-4 md:pl-0 p-5 rounded-lg">
      <div className="flex items-center justify-between gap-1 rounded-t-lg px-5 mb-4">
        <div>
          <h2 className="text-xl font-medium text-[#292524]">
            {id ? "Edit Banner" : "Add Banner"}
          </h2>
          <p className="mt-2">Web / Banners</p>
        </div>
      </div>

      <div className="mb-4 px-5">
        <form onSubmit={handleSubmit} className="space-y-4 w-full">

          <div className="flex items-center gap-2">
            <div>
              <label className="block mb-1 font-semibold" htmlFor="order_no">
                Order No
              </label>
              <input
                type="number"
                id="order_no"
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                className="border p-2 w-full"
                placeholder="Enter order number"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_enabled"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
              <label htmlFor="is_enabled" className="font-semibold">
                Enabled
              </label>
            </div>

          </div>


          <label className="block mb-1 font-semibold " htmlFor="banner">
            {id ? "Change Banner Image (optional)" : "Select Banner Image"}
          </label>
          <div
            className="w-full relative border-2 border-gray-300 border-dashed rounded-lg py-20"
            id="dropzone"
          >

            <input
              type="file"
              id="banner"
              accept="image/*"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 z-50"
            />
            <div className="text-center">
              <img
                className="mx-auto h-12 w-12"
                src="https://www.svgrepo.com/show/357902/image-upload.svg"
                alt=""
              />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                <label htmlFor="file-upload" className="relative cursor-pointer">
                  <span>Drag and drop</span>
                  <span className="text-indigo-600"> or browse</span>
                  <span>to upload</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                  />
                </label>
              </h3>
              <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
            </div>



          </div>

          {existingImage && !bannerFile && (
            <img
              src={`${process.env.REACT_APP_BASE_URL_API}/uploads/${existingImage}`}
              alt="Current Banner"
              className="mt-2 h-24 rounded"
            />
          )}








          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (id ? "Updating..." : "Uploading...") : id ? "Update Banner" : "Upload Banner"}
            </button>

            {id && (
              <button
                type="button"
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete Banner"}
              </button>
            )}
          </div>


          {message && <p className="mt-3 text-red-600">{message}</p>}
        </form>
      </div>
    </div>
  );
};

export default AddOrEditBanner;
